import { useRef, useState } from 'react'
import CaseLawReferenceCitatorTab from '@/citator/views/CaseLawReferenceCitatorTab'
import { CaseLawReferenceTab } from '@/citator/schema/case-law-reference-tab'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { CaselawReferenceMetadataResponseV3 } from '../schema/caselaw-reference-viewer.schema'
import { trackRiaSourceViewerTabChanged } from '@/analytics/utils/ria-source-analytics'
import { RiaViewerTab, type RiaCitatorSection } from '@/analytics/enums/ria.enums'
import type { RiaSourceContext } from '@/analytics/utils/ria-source-analytics'
import { HtmlReferenceLoadingSkeleton } from '@/chat-common/components/reference-viewer/caselaw/CaseLawOpinionLoadingSkeleton'
import { useOpinionCitationLinks } from '@/citator/hooks/useOpinionCitationLinks'
import { CaselawOpinionHtml } from '@/content-viewer/components/agent-reference/CaselawOpinionHtml'
import { CaseSummaryPanel } from '@/content-viewer/components/agent-reference/CaseSummaryPanel'
import type { CaselawViewerContext } from '../utils/build-caselaw-viewer-url'

type StandaloneReferenceViewCaselawTabsProps = {
  metadata: CaselawReferenceMetadataResponseV3
  /** Raw provider HTML. The opinion renderer owns sanitization before enrichment. */
  htmlContent: string | null | undefined
  isLoadingHtml: boolean
  isErrorHtml: boolean
  refetchHtml: () => void
  /** Analytics context (RIA), derived by the parent page. */
  analyticsContext: RiaSourceContext | null
  viewerContext: CaselawViewerContext
  /** Tab shown on load (deep link, e.g. an inline treatment flag lands on Citation Analysis). */
  initialTab?: CaseLawReferenceTab
  /** Citator accordion section opened on load (deep link, e.g. overruling risk expands case citations). */
  initialOpenSection?: RiaCitatorSection
}

export default function StandaloneReferenceViewCaselawTabs({
  metadata,
  htmlContent,
  isLoadingHtml,
  isErrorHtml,
  refetchHtml,
  analyticsContext,
  viewerContext,
  initialTab,
  initialOpenSection,
}: StandaloneReferenceViewCaselawTabsProps) {
  const { trackEvent } = useAnalytics()

  // Local state
  const containerRef = useRef<HTMLDivElement>(null)
  const [activeTab, setActiveTab] = useState<CaseLawReferenceTab>(initialTab ?? CaseLawReferenceTab.source)
  const { links: opinionCitationLinks } = useOpinionCitationLinks(metadata.parent_id)

  // Styles for tabs
  const baseTabStyles = 'grow w-1/2 min-h-[52px] py-3 border-b-2 text-center text-[15px] font-semibold font-rethink-sans'
  const inactiveTabStyles = `${baseTabStyles} border-transparent text-brand-neutral-500 hover:bg-brand-neutral-100 hover:text-brand-500`
  const activeTabStyles = `${baseTabStyles} border-brand-800 text-brand-800`

  // Handle tab change
  const trackTabChange = (nextTab: CaseLawReferenceTab) => {
    if (!analyticsContext) {
      return
    }

    const toTab = nextTab === CaseLawReferenceTab.citator ? RiaViewerTab.Citator : RiaViewerTab.SourceView
    const fromTab = activeTab === CaseLawReferenceTab.citator ? RiaViewerTab.Citator : RiaViewerTab.SourceView

    if (toTab === fromTab) {
      return
    }

    trackRiaSourceViewerTabChanged({
      trackEvent,
      context: analyticsContext,
      fromTab,
      toTab,
    })
  }

  function handleActiveTabChange(tab: CaseLawReferenceTab) {
    trackTabChange(tab)
    if (tab !== activeTab) {
      setActiveTab(tab)
    }
  }

  return (
    <>
      {/* Tabs */}
      <div className="border-b">
        <nav className="w-full flex items-center" aria-label="Tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === CaseLawReferenceTab.source}
            onClick={() => {
              handleActiveTabChange(CaseLawReferenceTab.source)
            }}
            className={activeTab == CaseLawReferenceTab.source ? activeTabStyles : inactiveTabStyles}
          >
            Opinion
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === CaseLawReferenceTab.citator}
            onClick={() => {
              handleActiveTabChange(CaseLawReferenceTab.citator)
            }}
            className={activeTab == CaseLawReferenceTab.citator ? activeTabStyles : inactiveTabStyles}
          >
            Citation Analysis
          </button>
        </nav>
      </div>
      {/* Render Source */}
      <div className={activeTab === CaseLawReferenceTab.source ? 'reference-view-html' : 'hidden'}>
        {isLoadingHtml && <HtmlReferenceLoadingSkeleton className="p-0" />}

        {(isErrorHtml || (!htmlContent && !isLoadingHtml)) && (
          <div className="p-4">
            <div className="text-red-700 text-sm">Error fetching data.</div>
            <button
              className="flex-auto items-center rounded-md bg-brand-500 border-[1px] border-brand-500 px-2 py-1 my-2 text-sm font-semibold text-white shadow-xs hover:bg-brand-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-400"
              onClick={() => refetchHtml()}
            >
              Retry
            </button>
          </div>
        )}

        {htmlContent && !isLoadingHtml && !isErrorHtml && (
          <div id="case-law-source" ref={containerRef} className="view-container bg-white w-full border border-brand-neutral-200 p-4">
            <CaseSummaryPanel key={metadata.parent_id} parentId={metadata.parent_id} />
            {activeTab === CaseLawReferenceTab.source && (
              <CaselawOpinionHtml htmlContent={htmlContent} citationLinks={opinionCitationLinks} viewerContext={viewerContext} />
            )}
          </div>
        )}
      </div>

      {/* Render Citator */}
      {activeTab === CaseLawReferenceTab.citator && (
        <CaseLawReferenceCitatorTab className="flex-1" metadata={metadata} analyticsContext={analyticsContext} defaultOpenSection={initialOpenSection} />
      )}
    </>
  )
}
